import React from 'react';
import {
    getOperationalMetricMeta,
    OPERATIONAL_METRIC_VALUE_TYPES,
} from '../../../constants/operationalMetricCatalog';
import {
    getMetricDisplayComponentProps,
    resolveMetricDisplayComponent,
} from './metricDisplayRegistry';
import {
    MetricEmptyState,
    MetricPanel,
    MetricPanelDescription,
    MetricPanelHeader,
    MetricPanelMeta,
    MetricPanelTitle,
    UnknownTypeNotice,
} from './styles';

function formatComputedAt(iso) {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
    });
}

function isMetricEmpty(metric) {
    const { valueType, data, layoutSlot } = metric;
    if (data == null) return true;
    if (valueType === OPERATIONAL_METRIC_VALUE_TYPES.RATE) {
        return data.rate == null || Number(data.denominator) === 0;
    }
    if (valueType === OPERATIONAL_METRIC_VALUE_TYPES.VOLUME_SERIES) {
        if (layoutSlot === 'volumeTotal') {
            return false;
        }
        return !Array.isArray(data.points) || data.points.length === 0;
    }
    if (Array.isArray(data)) return data.length === 0;
    if (typeof data === 'object' && 'value' in data) {
        return false;
    }
    return false;
}

const KPI_TYPES_ALWAYS_SHOW = new Set([
    OPERATIONAL_METRIC_VALUE_TYPES.SCALAR,
    OPERATIONAL_METRIC_VALUE_TYPES.MONEY,
    OPERATIONAL_METRIC_VALUE_TYPES.RATE,
]);

/**
 * @param {{ metric: { key: string, valueType: string, data: *, computedAt?: string | null, layoutSlot?: string }, loading?: boolean, hideHeader?: boolean, compactKpi?: boolean }} props
 */
export default function MetricDisplay({
    metric,
    loading = false,
    hideHeader = false,
    compactKpi = false,
}) {
    const { label, description } = getOperationalMetricMeta(metric.key);
    const Component = resolveMetricDisplayComponent(metric.valueType, metric.layoutSlot);
    const computedLabel = formatComputedAt(metric.computedAt);

    if (!Component) {
        return (
            <MetricPanel aria-label={label}>
                {!hideHeader ? (
                    <MetricPanelHeader>
                        <MetricPanelTitle>{label}</MetricPanelTitle>
                    </MetricPanelHeader>
                ) : null}
                <UnknownTypeNotice>
                    Tipo de métrica não suportado ({metric.valueType || 'desconhecido'}).
                </UnknownTypeNotice>
            </MetricPanel>
        );
    }

    const bodyProps = getMetricDisplayComponentProps(Component, metric, { loading, compactKpi });
    const skipEmptyForKpi = KPI_TYPES_ALWAYS_SHOW.has(metric.valueType)
        || metric.layoutSlot === 'volumeTotal';
    const showEmpty = !loading && isMetricEmpty(metric) && !skipEmptyForKpi;

    if (compactKpi) {
        return <Component {...bodyProps} />;
    }

    return (
        <MetricPanel aria-label={label} aria-busy={loading || undefined}>
            {!hideHeader ? (
                <MetricPanelHeader>
                    <MetricPanelTitle>{label}</MetricPanelTitle>
                    {description ? <MetricPanelDescription>{description}</MetricPanelDescription> : null}
                    {computedLabel ? (
                        <MetricPanelMeta>Atualizado em {computedLabel}</MetricPanelMeta>
                    ) : null}
                </MetricPanelHeader>
            ) : null}
            {showEmpty ? (
                <MetricEmptyState>Sem dados no período selecionado.</MetricEmptyState>
            ) : (
                <Component {...bodyProps} />
            )}
        </MetricPanel>
    );
}
